import { supabase } from '../config/supabase';

export enum AccountStatus {
  ACTIVE = 'active',
  PENDING_DELETION = 'pending_deletion',
  DELETED = 'deleted'
}

export interface EmailChangeResult {
  success: boolean;
  message: string;
  verificationSent?: boolean;
}

export interface EmailVerificationResult {
  success: boolean;
  message: string;
}

export interface UsernameAvailabilityResult {
  available: boolean;
  message?: string;
}

export interface PasswordChangeResult {
  success: boolean;
  message: string;
}

export interface AccountDeletionResult {
  success: boolean;
  message: string;
}

export interface PasswordResetResult {
  success: boolean;
  message: string;
}

/**
 * Service class for handling account management operations with Supabase
 */
export class AccountService {
  /**
   * Change user's email address
   * Sends verification email to new address
   */
  static async changeEmail(newEmail: string): Promise<EmailChangeResult> {
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) {
        // Handle specific Supabase auth errors
        if (error.message.includes('email_address_invalid')) {
          return {
            success: false,
            message: 'Please enter a valid email address'
          };
        }
        
        if (error.message.includes('email_address_not_authorized')) {
          return {
            success: false,
            message: 'This email address is not authorized for registration'
          };
        }
        
        if (error.message.includes('signup_disabled')) {
          return {
            success: false,
            message: 'Email changes are currently disabled'
          };
        }

        // Check for email already in use
        if (error.message.includes('already_registered') || 
            error.message.includes('email_address_taken') ||
            error.message.includes('User already registered')) {
          return {
            success: false,
            message: 'This email address is already associated with another account'
          };
        }

        // Generic error fallback
        return {
          success: false,
          message: error.message || 'Failed to change email address'
        };
      }

      return {
        success: true,
        message: `Verification email sent to ${newEmail}. Please check your inbox and click the verification link to complete the email change.`,
        verificationSent: true
      };
    } catch (error) {
      console.error('Email change error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while changing your email'
      };
    }
  }

  /**
   * Send a signup verification email to the provided address
   */
  static async sendSignupVerificationEmail(email: string): Promise<EmailVerificationResult> {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        }
      });

      if (error) {
        if (error.message?.includes('already confirmed')) {
          return {
            success: false,
            message: 'This email has already been confirmed'
          };
        }

        if (error.message?.includes('not found')) {
          return {
            success: false,
            message: 'No account found for this email address'
          };
        }

        return {
          success: false,
          message: error.message || 'Failed to send verification email'
        };
      }

      return {
        success: true,
        message: `Verification email sent to ${email}. Please check your inbox to confirm your account.`
      };
    } catch (error) {
      console.error('Send signup verification email error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while sending verification email'
      };
    }
  }

  /**
   * Verify email change using token from verification email
   */
  static async verifyEmailChange(token: string): Promise<EmailChangeResult> {
    try {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'email_change'
      });

      if (error) {
        if (error.message.includes('expired')) {
          return {
            success: false,
            message: 'Verification link has expired. Please request a new email change.'
          };
        }
        
        if (error.message.includes('invalid') || error.message.includes('Invalid')) {
          return {
            success: false,
            message: 'Invalid verification link. Please check the link or request a new one.'
          };
        }

        return {
          success: false,
          message: error.message || 'Failed to verify email change'
        };
      }

      return {
        success: true,
        message: 'Email address successfully updated!'
      };
    } catch (error) {
      console.error('Email verification error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred during email verification'
      };
    }
  }

  /**
   * Check if a username is available
   */
  static async checkUsernameAvailability(username: string): Promise<UsernameAvailabilityResult> {
    try {
      // Call the Supabase RPC function to check username availability
      const { data, error } = await supabase.rpc('check_username_availability', {
        p_username: username
      });

      if (error) {
        console.error('Username availability check error:', error);
        return {
          available: false,
          message: 'Unable to check username availability'
        };
      }

      return {
        available: data === true,
        message: data === true ? 'Username is available' : 'Username is already taken'
      };
    } catch (error) {
      console.error('Username availability check error:', error);
      return {
        available: false,
        message: 'Unable to check username availability'
      };
    }
  }

  /**
   * Change username
   */
  static async changeUsername(newUsername: string): Promise<EmailChangeResult> {
    try {
      // Call the Supabase RPC function to update username across all tables
      const { error } = await supabase.rpc('update_username_across_tables', {
        p_username: newUsername
      });

      if (error) {
        if (error.message.includes('username_taken')) {
          return {
            success: false,
            message: 'This username is already taken'
          };
        }

        return {
          success: false,
          message: error.message || 'Failed to update username'
        };
      }

      return {
        success: true,
        message: 'Username successfully updated!'
      };
    } catch (error) {
      console.error('Username change error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while updating your username'
      };
    }
  }

  /**
   * Change user's password
   */
  static async changePassword(currentPassword: string, newPassword: string): Promise<PasswordChangeResult> {
    try {
      // First verify the current password by attempting to sign in
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user?.email) {
        return {
          success: false,
          message: 'Unable to verify current user'
        };
      }

      // Verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });

      if (signInError) {
        return {
          success: false,
          message: 'Current password is incorrect'
        };
      }

      // Update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        if (updateError.message.includes('password_too_short')) {
          return {
            success: false,
            message: 'Password must be at least 6 characters long'
          };
        }

        return {
          success: false,
          message: updateError.message || 'Failed to update password'
        };
      }

      return {
        success: true,
        message: 'Password successfully updated!'
      };
    } catch (error) {
      console.error('Password change error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while changing your password'
      };
    }
  }

  /**
   * Delete user account (soft delete) — proxied through backend for server-side CAPTCHA
   */
  static async deleteAccount(password: string, captchaToken?: string): Promise<AccountDeletionResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return {
          success: false,
          message: 'Unable to verify current user'
        };
      }

      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/auth/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ password, captchaToken }),
      });

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          message: data.message || data.error || 'Failed to delete account'
        };
      }

      // Sign out the user locally
      await supabase.auth.signOut();

      return {
        success: true,
        message: 'Account successfully deleted. You have been signed out.'
      };
    } catch (error) {
      console.error('Account deletion error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while deleting your account'
      };
    }
  }

  /**
   * Check account status
   */
  static async checkAccountStatus(userId: string): Promise<AccountStatus> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('account_status')
        .eq('id', userId)
        .single();

      if (error || !data) {
        return AccountStatus.ACTIVE; // Default to active if we can't determine status
      }

      return data.account_status as AccountStatus || AccountStatus.ACTIVE;
    } catch (error) {
      console.error('Account status check error:', error);
      return AccountStatus.ACTIVE;
    }
  }

  /**
   * Request password reset email — proxied through backend for server-side CAPTCHA
   * NOTE: For security, always returns success even if email doesn't exist
   */
  static async requestPasswordReset(email: string, captchaToken?: string): Promise<PasswordResetResult> {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      await fetch(`${apiBaseUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, captchaToken }),
      });

      // Always return success to prevent email enumeration
      return {
        success: true,
        message: 'If an account exists with this email, you will receive a password reset link shortly. Please check your inbox.'
      };
    } catch (error) {
      console.error('Password reset request error:', error);
      return {
        success: true,
        message: 'If an account exists with this email, you will receive a password reset link shortly. Please check your inbox.'
      };
    }
  }

  /**
   * Verify password reset token
   * This verifies the token from the password reset email
   */
  static async verifyPasswordResetToken(token: string): Promise<EmailChangeResult> {
    try {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'recovery'
      });

      if (error) {
        if (error.message.includes('expired') || error.message.includes('Expired')) {
          return {
            success: false,
            message: 'Password reset link has expired. Please request a new one.'
          };
        }

        if (error.message.includes('invalid') || error.message.includes('Invalid')) {
          return {
            success: false,
            message: 'Invalid password reset link. Please check the link or request a new one.'
          };
        }

        return {
          success: false,
          message: error.message || 'Failed to verify password reset link'
        };
      }

      return {
        success: true,
        message: 'Password reset link verified successfully!'
      };
    } catch (error) {
      console.error('Password reset token verification error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred during verification'
      };
    }
  }

  /**
   * Confirm password reset with new password
   * This updates the user's password after they've clicked the reset link
   */
  static async confirmPasswordReset(newPassword: string): Promise<PasswordChangeResult> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        if (error.message.includes('password_too_short')) {
          return {
            success: false,
            message: 'Password must be at least 6 characters long'
          };
        }

        if (error.message.includes('same_password') || error.message.includes('Same password')) {
          return {
            success: false,
            message: 'New password must be different from your previous password'
          };
        }

        return {
          success: false,
          message: error.message || 'Failed to reset password'
        };
      }

      return {
        success: true,
        message: 'Password successfully reset! You can now sign in with your new password.'
      };
    } catch (error) {
      console.error('Password reset error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while resetting your password'
      };
    }
  }
}
